import { Link } from "react-router-dom";
import { Check, Sparkles, Tv, Film, Smartphone, Monitor, Headphones, Zap, ArrowRight } from "lucide-react";
import Nav from "@/saas/components/Nav";
import Footer from "@/saas/components/Footer";
import SButton from "@/saas/components/SButton";
import { useI18n } from "@/saas/i18n";
import { PLANS, formatPrice } from "@/saas/config/plans";

const HERO_IMG =
  "/images/landing-hero.jpg";

export default function Landing() {
  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-bg text-white">
      <Nav />

      {/* ========== HERO ========== */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt=""
            className="w-full h-full object-cover opacity-60 scale-105 animate-[heroBreath_24s_ease-in-out_infinite_alternate]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/60 to-bg" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-32 w-full">
          <div className="max-w-2xl animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/40 bg-gold/10 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-gold" />
              <span className="text-[11px] font-mono uppercase tracking-widest text-gold">{t("hero.kicker")}</span>
            </div>
            <h1 className="font-display font-extrabold tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6 text-shadow-strong">
              {t("hero.title")}
            </h1>
            <p className="text-lg text-white/75 max-w-xl mb-8 leading-relaxed">{t("hero.subtitle")}</p>
            <div className="flex flex-wrap gap-3">
              <SButton to="/plans"  size="lg" iconRight={ArrowRight} data-testid="hero-cta-plans">
                {t("hero.cta.primary")}
              </SButton>
              <SButton to="/login"  size="lg" variant="outline" data-testid="hero-cta-login">
                {t("hero.cta.ghost")}
              </SButton>
            </div>
            <p className="text-xs text-white/50 font-mono mt-6">{t("hero.trust")}</p>
          </div>
        </div>

        {/* subtle grain */}
        <div className="absolute inset-0 noise pointer-events-none" />

        {/* inline keyframes for the breathing hero (scoped via Tailwind arbitrary) */}
        <style>{`
          @keyframes heroBreath {
            from { transform: scale(1.02); }
            to   { transform: scale(1.1); }
          }
        `}</style>
      </section>

      {/* ========== FEATURES ========== */}
      <section className="relative py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <div className="text-[11px] font-mono uppercase tracking-widest text-gold mb-3">{t("section.features.kicker")}</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]">
              {t("section.features.title")}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { Icon: Film,       k: "feature.4k" },
              { Icon: Tv,         k: "feature.library" },
              { Icon: Smartphone, k: "feature.devices" },
              { Icon: Headphones, k: "feature.support" },
            ].map(({ Icon, k }) => (
              <div
                key={k}
                className="group p-6 rounded-xl border border-white/10 bg-surface/80 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-lg bg-gold/10 border border-gold/25 grid place-items-center mb-5 group-hover:bg-gold/20 transition">
                  <Icon className="w-5 h-5 text-gold" />
                </div>
                <h3 className="font-display text-xl font-semibold mb-2">{t(`${k}.title`)}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{t(`${k}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== PLANS TEASER ========== */}
      <section className="relative py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-14 flex items-end justify-between flex-wrap gap-6">
            <div className="max-w-xl">
              <div className="text-[11px] font-mono uppercase tracking-widest text-gold mb-3">{t("section.plans.kicker")}</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05] mb-3">
                {t("section.plans.title")}
              </h2>
              <p className="text-white/60">{t("section.plans.subtitle")}</p>
            </div>
            <SButton to="/plans" variant="outline" iconRight={ArrowRight}>
              {t("nav.plans")}
            </SButton>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {PLANS.map((p) => (
              <MiniPlanCard key={p.id} plan={p} />
            ))}
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(245,166,35,0.18),_transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/40 bg-gold/10 mb-6">
            <Zap className="w-3.5 h-3.5 text-gold" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-gold">{t("hero.kicker")}</span>
          </div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-5 leading-[1.05]">
            {t("section.cta.title")}
          </h2>
          <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">{t("section.cta.subtitle")}</p>
          <SButton to="/register" size="lg" iconRight={ArrowRight} data-testid="landing-cta-register">
            {t("section.cta.button")}
          </SButton>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function MiniPlanCard({ plan }) {
  const { t } = useI18n();
  const isFeatured = plan.featured;
  return (
    <Link
      to="/plans"
      className={`relative block p-6 rounded-xl border transition-all duration-300 ${
        isFeatured
          ? "border-gold/60 bg-gradient-to-b from-gold/10 to-transparent shadow-[0_24px_80px_-32px_rgba(245,166,35,0.55)]"
          : "border-white/10 bg-surface/60 hover:border-white/25"
      }`}
    >
      {plan.badge && (
        <span className="absolute -top-2.5 right-5 text-[10px] font-mono uppercase tracking-widest bg-gold text-black px-2 py-0.5 rounded-full">
          {t(plan.badge)}
        </span>
      )}
      <div className="text-[11px] font-mono uppercase tracking-widest text-white/50 mb-2">
        {t("plans.months", { n: plan.months })}
      </div>
      <div className="flex items-baseline gap-1 mb-3">
        <span className="font-display text-5xl font-extrabold">{formatPrice(plan.pricePerMonth)}</span>
        <span className="text-sm text-white/50">{t("plans.per_month")}</span>
      </div>
      <div className="text-xs text-white/50 mb-6">
        {t("plans.billed_total", { amount: formatPrice(plan.priceTotal) })}
      </div>
      <ul className="space-y-2">
        {plan.features.slice(0, 4).map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm text-white/75">
            <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
            <span>{t(f)}</span>
          </li>
        ))}
      </ul>
    </Link>
  );
}
